import { useState, useEffect } from "react";
import { ASSETS } from "@/lib/assets";
import { waLink, PRESET } from "@/lib/whatsapp";

const HERO_IMAGES = [
  // May's work first
  "/images/artists/may/may_1.jpeg",
  "/images/artists/may/may_2.jpeg",
  "/images/artists/may/may_3.jpeg",
  "/images/artists/may/may_4.jpeg",
  "/images/artists/may/may_5.jpeg",
  // Then other artists
  "/images/portfolio/eeme_floral.jpeg",
  "/images/portfolio/eeme_virgin.jpeg",
  "/images/portfolio/alvaro_lion.jpeg",
  "/images/portfolio/alvaro_mandala_chest.jpeg",
  "/images/portfolio/alvaro_quetzalcoatl.jpeg",
  "/images/portfolio/eeme_chichen.jpeg",
  "/images/portfolio/tyson_nose.jpeg",
];

export const Hero = ({ t, lang }) => {
  const [currentIdx, setCurrentIdx] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIdx((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section
      id="top"
      className="relative min-h-[100svh] w-full overflow-hidden flex items-end"
      data-testid="hero-section"
    >
      {HERO_IMAGES.map((src, i) => (
        <img
          key={src}
          src={src}
          alt="Tattoo work by Bendito Tattoo"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
            i === currentIdx ? "opacity-100 ken-burns-active" : "opacity-0"
          }`}
          style={i !== currentIdx ? { animation: "none" } : undefined}
          loading={i === 0 ? "eager" : "lazy"}
          fetchPriority={i === 0 ? "high" : "low"}
        />
      ))}
      {/* Layered gradient — keeps photo punchy on top, fades to brand background */}
      <div className="absolute inset-0 bg-gradient-to-b from-bendito-bg/30 via-bendito-bg/55 to-bendito-bg" />
      <div className="absolute inset-0 bg-gradient-to-tr from-bendito-bg/70 via-transparent to-transparent" />
      <div className="absolute inset-0 grain pointer-events-none" />

      {/* Vertical decoration bar — desktop only */}
      <div className="absolute top-0 bottom-0 left-5 md:left-8 flex-col items-center justify-center gap-4 z-10 pointer-events-none hidden lg:flex">
        <span className="w-px h-24 bg-bendito-ink/20" />
        <span className="font-mono text-[10px] tracking-[0.4em] text-bendito-ink/40 rotate-90 origin-center whitespace-nowrap">
          EST · MMXXIV
        </span>
        <span className="w-px h-24 bg-bendito-ink/20" />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-5 md:px-8 lg:px-12 pb-20 md:pb-28 pt-32">
        <div className="max-w-4xl">
          <div className="eyebrow mb-6 flex items-center gap-3 hero-line">
            <span className="inline-block w-8 h-px bg-bendito-orange" />
            {t.hero.eyebrow}
          </div>
          <h1 className="font-heading text-bendito-ink text-4xl sm:text-5xl md:text-7xl lg:text-[110px] leading-[0.92] tracking-tight uppercase">
            <span className="block hero-title hero-title-1">{t.hero.title1}</span>
            <span className="block text-bendito-orange hero-title hero-title-2">
              {t.hero.title2}
            </span>
          </h1>
          <p className="mt-7 text-bendito-muted text-base md:text-lg max-w-xl leading-relaxed hero-line hero-line-2">
            {t.hero.sub}
          </p>

          <div className="mt-9 flex flex-col sm:flex-row gap-4 hero-line hero-line-3">
            <a
              href={waLink(lang === "es" ? PRESET.generalEs : PRESET.generalEn)}
              target="_blank"
              rel="noopener noreferrer"
              className="cta-magnet inline-flex items-center justify-center bg-bendito-orange hover:bg-bendito-orangeDark text-black font-heading tracking-[0.22em] text-base px-8 py-4 transition-colors"
              data-testid="hero-cta-primary"
            >
              {t.hero.ctaPrimary}
            </a>
            <a
              href="#work"
              className="cta-magnet inline-flex items-center justify-center border border-bendito-ink hover:bg-bendito-ink hover:text-black text-bendito-ink font-heading tracking-[0.22em] text-base px-8 py-4 transition-colors"
              data-testid="hero-cta-secondary"
            >
              {t.hero.ctaSecondary}
            </a>
          </div>

          {/* Late-night badge */}
          <div className="mt-7 inline-flex items-center gap-2 text-[9px] sm:text-[11px] font-mono tracking-[0.15em] sm:tracking-[0.25em] uppercase text-bendito-ink/80 hero-line hero-line-4">
            <span className="relative flex h-2 w-2">
              <span className="absolute inset-0 rounded-full bg-bendito-orange animate-ping opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-bendito-orange" />
            </span>
            {lang === "es"
              ? "Abierto hasta medianoche · cupos nocturnos limitados"
              : "Open till midnight · limited late-night slots"}
          </div>
        </div>

        <div className="hidden md:flex items-center gap-3 mt-16 text-bendito-muted hero-line hero-line-5">
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase">{t.hero.scroll}</span>
          <span className="block w-px h-10 bg-bendito-muted/40 relative overflow-hidden">
            <span className="absolute inset-x-0 top-0 h-3 bg-bendito-orange animate-scroll-hint" />
          </span>
        </div>
      </div>

      <style>{`
        @keyframes hero-fade-up {
          0%   { opacity: 0; transform: translateY(28px); filter: blur(6px); }
          100% { opacity: 1; transform: translateY(0); filter: blur(0); }
        }
        .hero-title, .hero-line {
          opacity: 0;
          animation: hero-fade-up 1.1s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .hero-title-1 { animation-delay: 0.25s; }
        .hero-title-2 { animation-delay: 0.45s; }
        .hero-line   { animation-delay: 0.1s; }
        .hero-line-2 { animation-delay: 0.7s; }
        .hero-line-3 { animation-delay: 0.85s; }
        .hero-line-4 { animation-delay: 1.0s; }
        .hero-line-5 { animation-delay: 1.15s; }
      `}</style>
    </section>
  );
};

export default Hero;
